import { useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Save, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const SECTION_TYPES = [
  { value: "hero", label: "Hero Banner", desc: "Big banner image at the top of the page" },
  { value: "marquee", label: "Marquee Strip", desc: "Scrolling brand text strip" },
  { value: "categories", label: "Categories", desc: "List of shoppable categories" },
  { value: "best_sellers", label: "Best Sellers", desc: "Auto-fills products with the Best Seller badge" },
  { value: "trending", label: "Trending Now", desc: "Auto-fills products with the Trending badge" },
  { value: "featured", label: "Featured Designs", desc: "Newest products" },
  { value: "custom_grid", label: "Custom Product Grid", desc: "Pick specific products to feature" },
  { value: "testimonials", label: "Customer Reviews", desc: "Auto-pulls 5-star customer reviews" },
  { value: "cta", label: "Call-to-Action Banner", desc: "Promotional banner with a button" },
];
const TYPE_META = Object.fromEntries(SECTION_TYPES.map((t) => [t.value, t]));

export default function HomeSectionsManager() {
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState("custom_grid");

  const fetchAll = async () => {
    const [s, p] = await Promise.all([
      api.get("/admin/home/sections"),
      api.get("/products"),
    ]);
    setSections(s.data);
    setProducts(p.data);
  };

  useEffect(() => { fetchAll(); }, []);

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    const items = next.map((s, i) => ({ id: s.id, position: i }));
    setSections(next.map((s, i) => ({ ...s, position: i })));
    try {
      await api.post("/admin/home/sections/reorder", { items });
      toast.success("Reordered");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reorder failed");
      fetchAll();
    }
  };

  const toggle = async (s) => {
    try {
      const { data } = await api.patch(`/admin/home/sections/${s.id}`, { enabled: !s.enabled });
      setSections((arr) => arr.map((x) => (x.id === s.id ? data : x)));
      toast.success(data.enabled ? "Section visible" : "Section hidden");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the "${s.title || s.type}" section?`)) return;
    try {
      await api.delete(`/admin/home/sections/${s.id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const addNew = async () => {
    try {
      const meta = TYPE_META[newType];
      await api.post("/admin/home/sections", {
        type: newType,
        title: meta?.label || newType,
        subtitle: "",
        enabled: true,
        config: newType === "custom_grid" ? { product_ids: [], limit: 4 }
              : newType === "cta" ? { body: "", button_label: "Shop Everything", button_link: "/shop" }
              : (newType === "best_sellers" || newType === "trending" || newType === "featured") ? { limit: newType === "featured" ? 8 : 4 }
              : {},
      });
      toast.success("Section added");
      setAdding(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const saveEdit = async (form) => {
    try {
      const { data } = await api.patch(`/admin/home/sections/${form.id}`, {
        title: form.title,
        subtitle: form.subtitle,
        enabled: form.enabled,
        config: form.config,
      });
      setSections((arr) => arr.map((x) => (x.id === form.id ? data : x)));
      toast.success("Saved");
      setEditing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="sections-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-[#4a4239] text-sm">Drag the arrows to reorder. Toggle visibility with the eye icon. Edit titles and product picks per section.</p>
        <button onClick={() => setAdding(true)} data-testid="add-section" className="inline-flex items-center gap-2 bg-[#e8478e] hover:bg-[#c93875] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs px-5 py-3 rounded-full transition-all">
          <Plus size={14}/> Add Section
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {sections.map((s, i) => (
          <div key={s.id} data-testid={`section-row-${s.type}`} className={`p-5 rounded-2xl border bg-white shadow-sm flex items-center gap-4 ${s.enabled ? "border-[#e5dccd]" : "border-[#e5dccd] opacity-60"}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`up-${s.id}`} className="p-1 text-[#4a4239] hover:text-[#2eb6a8] disabled:opacity-30"><ChevronUp size={16}/></button>
              <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} data-testid={`down-${s.id}`} className="p-1 text-[#4a4239] hover:text-[#2eb6a8] disabled:opacity-30"><ChevronDown size={16}/></button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8076] font-bold">{TYPE_META[s.type]?.label || s.type}</div>
              <div className="font-display font-bold text-lg text-[#1c1815] truncate">{s.title || TYPE_META[s.type]?.label || s.type}</div>
              {s.subtitle && <div className="font-script text-base text-[#e8478e]">{s.subtitle}</div>}
              <div className="text-xs text-[#8a8076] mt-0.5 truncate">{TYPE_META[s.type]?.desc}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => toggle(s)} data-testid={`toggle-${s.id}`} title={s.enabled ? "Hide" : "Show"} className={`p-2 rounded-md ${s.enabled ? "text-[#2eb6a8] hover:bg-[#2eb6a8]/10" : "text-[#8a8076] hover:bg-[#efe5d4]"}`}>
                {s.enabled ? <Eye size={16}/> : <EyeOff size={16}/>}
              </button>
              <button onClick={() => setEditing({ ...s, config: s.config || {} })} data-testid={`edit-section-${s.id}`} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#faf4e9] hover:bg-[#efe5d4] rounded-md text-[#1c1815]">Edit</button>
              <button onClick={() => remove(s)} data-testid={`delete-section-${s.id}`} className="p-2 hover:bg-[#e8478e]/10 rounded-md text-[#e8478e]"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
        {sections.length === 0 && <div className="p-12 text-center text-[#8a8076] border border-[#e5dccd] rounded-2xl">No sections yet.</div>}
      </div>

      {adding && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#1c1815]/45 backdrop-blur-sm" onClick={() => setAdding(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#e5dccd] rounded-3xl w-full max-w-md p-7 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-extrabold text-xl text-[#1c1815]">Add Section</h3>
              <button onClick={() => setAdding(false)} className="p-2 hover:bg-[#efe5d4] rounded-full"><X size={18}/></button>
            </div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Section type</label>
            <div className="mt-2 space-y-2 max-h-80 overflow-y-auto">
              {SECTION_TYPES.map((t) => (
                <label key={t.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${newType === t.value ? "border-[#2eb6a8] bg-[#2eb6a8]/5" : "border-[#e5dccd] hover:border-[#2eb6a8]"}`}>
                  <input type="radio" name="newType" value={t.value} checked={newType === t.value} onChange={() => setNewType(t.value)} className="mt-1 accent-[#2eb6a8]"/>
                  <div>
                    <div className="font-bold text-sm text-[#1c1815]">{t.label}</div>
                    <div className="text-xs text-[#8a8076]">{t.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <button onClick={addNew} data-testid="confirm-add-section" className="w-full mt-5 bg-[#2eb6a8] hover:bg-[#1f8d83] text-white font-bold uppercase tracking-[0.2em] text-xs py-3 rounded-full">
              Add To Homepage
            </button>
          </div>
        </div>
      )}

      {editing && <SectionEditor section={editing} products={products} onCancel={() => setEditing(null)} onSave={saveEdit}/>}
    </div>
  );
}

function SectionEditor({ section, products, onCancel, onSave }) {
  const [form, setForm] = useState({ ...section, config: { ...(section.config || {}) } });
  const isProductGrid = ["best_sellers", "trending", "featured", "custom_grid"].includes(form.type);
  const isCustomGrid = form.type === "custom_grid";
  const isCta = form.type === "cta";
  const isHero = form.type === "hero";

  const toggleProduct = (id) => {
    const ids = form.config.product_ids || [];
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    setForm((f) => ({ ...f, config: { ...f.config, product_ids: next } }));
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 sm:p-8 overflow-y-auto bg-[#1c1815]/45 backdrop-blur-sm" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#e5dccd] rounded-3xl w-full max-w-2xl p-7 sm:p-9 my-8 shadow-2xl" data-testid="section-editor">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Edit Section</div>
            <h3 className="font-display font-extrabold text-2xl text-[#1c1815]">{TYPE_META[form.type]?.label || form.type}</h3>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-[#efe5d4] rounded-full"><X size={20}/></button>
        </div>

        <div className="space-y-4">
          <Field label="Title" value={form.title || ""} onChange={(v) => setForm({ ...form, title: v })} testid="edit-title"/>
          <Field label="Subtitle (script accent)" value={form.subtitle || ""} onChange={(v) => setForm({ ...form, subtitle: v })} testid="edit-subtitle" placeholder="e.g. Customer Obsessions"/>

          {isHero && (
            <Field label="Banner image URL (optional — uses default if blank)" value={form.config.image_url || ""} onChange={(v) => setForm({ ...form, config: { ...form.config, image_url: v } })} testid="edit-hero-image"/>
          )}

          {isProductGrid && !isCustomGrid && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Number of products to show</label>
              <input type="number" min={1} max={12} value={form.config.limit ?? 4} onChange={(e) => setForm({ ...form, config: { ...form.config, limit: Number(e.target.value) } })}
                className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] rounded-xl px-4 py-3 outline-none text-[#1c1815]"/>
            </div>
          )}

          {isCustomGrid && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Pick products to feature</label>
              <div className="mt-2 max-h-72 overflow-y-auto border border-[#e5dccd] rounded-xl p-2 bg-[#faf4e9]">
                {products.map((p) => {
                  const checked = (form.config.product_ids || []).includes(p.id);
                  return (
                    <label key={p.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${checked ? "bg-[#2eb6a8]/10" : "hover:bg-white"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleProduct(p.id)} className="accent-[#2eb6a8]"/>
                      <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded-md"/>
                      <div className="flex-1 min-w-0 text-sm">
                        <div className="font-bold text-[#1c1815] truncate">{p.title}</div>
                        <div className="text-xs text-[#8a8076]">${p.price.toFixed(2)} · {p.category}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="text-xs text-[#8a8076] mt-1">{(form.config.product_ids || []).length} selected</div>
            </div>
          )}

          {isCta && (
            <>
              <Field label="Body text (optional)" value={form.config.body || ""} onChange={(v) => setForm({ ...form, config: { ...form.config, body: v } })} testid="edit-cta-body"/>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Button label" value={form.config.button_label || ""} onChange={(v) => setForm({ ...form, config: { ...form.config, button_label: v } })} testid="edit-cta-label"/>
                <Field label="Button link" value={form.config.button_link || ""} onChange={(v) => setForm({ ...form, config: { ...form.config, button_link: v } })} testid="edit-cta-link"/>
              </div>
            </>
          )}

          <label className="flex items-center gap-3 pt-2">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-[#2eb6a8]"/>
            <span className="text-sm text-[#1c1815]">Visible on the homepage</span>
          </label>

          <button onClick={() => onSave(form)} data-testid="save-section" className="w-full mt-3 bg-[#2eb6a8] hover:bg-[#1f8d83] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs py-3.5 rounded-full transition-all inline-flex items-center justify-center gap-2">
            <Save size={14}/> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">{label}</label>
      <input type="text" value={value} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none text-[#1c1815]"/>
    </div>
  );
}
